import styleVariables from '@/assets/scss/variables.module.scss';
import { computed } from 'vue';
import useTranslate from '@/utilities/composables/useTranslate';
import { TeamMemberRole } from '@superthread-com/api';
import constants from '@/utilities/constants';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

const SPACE_LIMIT_ID = constants.limitIds.spaceLimit;

export default function useUsageLimits() {
  const { translate } = useTranslate();
  const store = useStore();
  const router = useRouter();

  const teamLimits = computed(() => {
    return store.getters.getTeam?.limits ?? [];
  });

  const spaceLimit = computed(() => {
    return teamLimits.value.find((limit: any) => limit.id === SPACE_LIMIT_ID)?.limit_value ?? 0;
  });

  const subscriptionPlan = computed(() => {
    return store.getters.getSubscriptionPlan;
  });

  const freePlan = computed(() => {
    return subscriptionPlan.value === 'free';
  });

  const pageUsageLimit = computed(() => {
    return (
      teamLimits.value.find((limit: any) => limit.id === constants.limitIds.pageSize)
        ?.limit_value ?? 0
    );
  });

  const createdSpaces = computed(() => {
    return store.getters.getTeamProjects.length;
  });

  const currentRole = computed(() => {
    return store.getters.getCurrentRole;
  });

  const userHasAccessToBilling = computed(() => {
    return [TeamMemberRole.Owner, TeamMemberRole.Admin].includes(currentRole.value);
  });

  const spacesLimitReached = computed(() => {
    return !!spaceLimit.value && createdSpaces.value >= spaceLimit.value;
  });

  const routeToUpgradePlan = () => {
    if (!userHasAccessToBilling.value) return;
    router.push({
      name: constants.routeNames.billing,
    });
  };

  const spacesPercentageUsed = computed(() => {
    return Math.min(Math.round((createdSpaces.value / spaceLimit.value) * 100), 100);
  });

  const spacesUsageProgressBarColor = computed(() => {
    return spacesPercentageUsed.value >= 100 ? styleVariables.error500 : styleVariables.warning400;
  });

  const spacesUsageCircularProgressBarColor = computed(() => {
    return spacesPercentageUsed.value >= 100 ? 'red' : 'orange';
  });

  const spacesUsageText = computed(() => {
    return `${createdSpaces.value}/${spaceLimit.value}`;
  });

  const spacesUsageWithPercentageText = computed(() => {
    let text = spacesUsageText.value;
    if (spacesPercentageUsed.value >= 100) text += ` (${translate('limitReached')})`;
    else text += ` (${spacesPercentageUsed.value}%)`;

    return text;
  });

  const spacesProgressBarData = computed(() => {
    return [
      {
        percentage: spacesPercentageUsed.value,
        color: spacesUsageProgressBarColor.value,
      },
    ];
  });

  return {
    freePlan,
    createdSpaces,
    currentRole,
    userHasAccessToBilling,
    routeToUpgradePlan,
    spacesPercentageUsed,
    spacesUsageProgressBarColor,
    spacesUsageCircularProgressBarColor,
    spacesUsageText,
    spacesUsageWithPercentageText,
    spacesProgressBarData,
    spaceLimit,
    spacesLimitReached,
    pageUsageLimit,
  };
}
